import React from 'react';
import { History, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export const SECTION_LABEL = { probound: 'ProBound', proepoxy: 'ProEpoxy', prodecor: 'ProDecor' };

// Balance-payment methods — matches the backend Literal on
// /staff/.../registrations/{id}/payments. Displayed in the "Record payment"
// modal and echoed back as a small chip once a payment is recorded.
// Payment methods the user chooses from when logging a fresh payment
// on the register. Stored values are human-readable so the popover +
// exports read cleanly without a lookup table. If new options are
// added, mirror them anywhere payment_method is displayed.
export const PAYMENT_METHODS = [
  { key: 'Bank Transfer',              label: 'Bank Transfer' },
  { key: 'Stripe (Card)',              label: 'Stripe (Card)' },
  { key: 'Worldpay (Card)',            label: 'Worldpay (Card)' },
  { key: 'Worldpay (Card Payment Link)', label: 'Worldpay (Card Payment Link)' },
  { key: 'Cash',                       label: 'Cash' },
  { key: 'Xero Invoice',               label: 'Xero Invoice' },
];
export const PAYMENT_METHOD_LABEL = Object.fromEntries(PAYMENT_METHODS.map(m => [m.key, m.label]));

/**
 * PaymentHistoryPopover — click/tap the trigger to see every payment recorded
 * against a trainee's balance. Radix `Popover` is used (not `HoverCard`) so
 * the same UX works on desktop and touch devices; on desktop it also opens
 * on Tab-focus + Space.
 *
 * Optional `onDelete(originalIndex)` — when supplied, each entry gets a
 * trash button that calls it with the ORIGINAL (stored, oldest-first)
 * index, not the reversed display index. Handy for staff to correct a
 * mis-parsed spreadsheet import in-place.
 *
 * Optional `onEdit(originalIndex, patch)` — enables an inline edit form
 * on a pencil click. `patch` is `{amount?, method?, at?}` — send only
 * what changed. Same index semantics as `onDelete`.
 *
 * Optional `onAdd(payload)` — enables the "Add a payment" button at the
 * top of the popover. `payload` is `{amount, method, at}`. Backend
 * appends the entry and recomputes totals.
 */
export function PaymentHistoryPopover({ history, trigger, testid, onDelete, deletingIndex, onEdit, editingIndex, onAdd, addingPayment, defaultAmount }) {
  const entries = Array.isArray(history) ? history : [];
  const [editRowIdx, setEditRowIdx] = React.useState(null);
  const [editDraft, setEditDraft] = React.useState({ amount: '', method: '', at: '' });
  // Add-a-payment form state — collapsed by default so the popover
  // stays scannable. Amount pre-fills with the outstanding balance so
  // the common "clear the balance" tap is one click away.
  const [addOpen, setAddOpen] = React.useState(false);
  const [addDraft, setAddDraft] = React.useState({
    amount: defaultAmount != null && defaultAmount > 0 ? String(defaultAmount) : '',
    method: PAYMENT_METHODS[0].key,
    at: new Date().toISOString().slice(0, 10),
    kind: 'payment',
  });
  const isRefund = addDraft.kind === 'refund';

  const beginEdit = (originalIdx, entry) => {
    const iso = entry.at
      ? (typeof entry.at === 'string' ? entry.at.slice(0, 10) : new Date(entry.at).toISOString().slice(0, 10))
      : '';
    setEditDraft({
      amount: String(Number(entry.amount || 0)),
      method: entry.method || '',
      at: iso,
    });
    setEditRowIdx(originalIdx);
  };
  const cancelEdit = () => setEditRowIdx(null);
  const saveEdit = async () => {
    const idx = editRowIdx;
    if (idx === null) return;
    const patch = {};
    const amtNum = Number(editDraft.amount);
    if (Number.isFinite(amtNum) && amtNum >= 0) patch.amount = amtNum;
    // Send method even when blank — backend treats "" as "clear the method".
    patch.method = editDraft.method;
    if (editDraft.at) patch.at = editDraft.at;
    await onEdit(idx, patch);
    setEditRowIdx(null);
  };

  const submitAdd = async () => {
    const amt = Number(addDraft.amount);
    if (!Number.isFinite(amt) || amt <= 0) return;
    await onAdd({ amount: amt, method: addDraft.method, at: addDraft.at, kind: addDraft.kind });
    // Reset for the next add — keep the method the user just picked
    // because staff often log consecutive same-method payments (e.g.
    // 3 BT payments in a row on batch reconcile day).
    setAddDraft((d) => ({ ...d, amount: '', at: new Date().toISOString().slice(0, 10), kind: 'payment' }));
    setAddOpen(false);
  };

  return (
    <Popover onOpenChange={(open) => { if (!open) { setEditRowIdx(null); setAddOpen(false); } }}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 p-0 !bg-white"
        style={{ background: '#FFFFFF' }}
        data-testid={testid}
      >
        <div className="p-3 border-b flex items-center gap-2">
          <History className="w-4 h-4 text-gray-400" />
          <div className="text-sm font-semibold text-gray-900">Payment history</div>
          <span className="ml-auto text-[11px] text-gray-500">{entries.length} entr{entries.length === 1 ? 'y' : 'ies'}</span>
        </div>
        {onAdd && (
          <div className="p-3 border-b bg-emerald-50/40">
            {!addOpen ? (
              <button
                type="button"
                onClick={() => setAddOpen(true)}
                className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-bold text-emerald-800 hover:text-emerald-900 bg-white border border-emerald-300 rounded-md px-2 py-1.5 transition"
                data-testid="payment-history-add-open"
              >
                <Plus className="w-3.5 h-3.5" />
                Add a payment
              </button>
            ) : (
              <div className="space-y-2" data-testid="payment-history-add-form">
                <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold">
                  {[['payment', 'Payment'], ['refund', 'Refund']].map(([k, l]) => (
                    <button key={k} type="button" onClick={() => setAddDraft(d => ({ ...d, kind: k }))}
                      className={`px-2 py-0.5 rounded-full border ${addDraft.kind === k ? (k === 'refund' ? 'bg-rose-600 border-rose-600 text-white' : 'bg-emerald-600 border-emerald-600 text-white') : 'bg-white border-gray-300 text-gray-600'}`}
                      data-testid={`payment-history-kind-${k}`}>{l}</button>
                  ))}
                  <span className={`ml-auto ${isRefund ? 'text-rose-800' : 'text-emerald-800'}`}>{isRefund ? 'Record a refund' : 'Log a payment'}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <label className="text-[11px] text-gray-600">
                    Amount (£)
                    <input
                      type="number" step="0.01" min="0.01"
                      value={addDraft.amount}
                      onChange={(ev) => setAddDraft(d => ({ ...d, amount: ev.target.value }))}
                      className="w-full mt-0.5 px-2 py-1 text-sm border border-gray-300 rounded bg-white"
                      data-testid="payment-history-add-amount"
                      autoFocus
                    />
                  </label>
                  <label className="text-[11px] text-gray-600">
                    Date
                    <input
                      type="date"
                      value={addDraft.at}
                      onChange={(ev) => setAddDraft(d => ({ ...d, at: ev.target.value }))}
                      className="w-full mt-0.5 px-2 py-1 text-sm border border-gray-300 rounded bg-white"
                      data-testid="payment-history-add-date"
                    />
                  </label>
                </div>
                <label className="block text-[11px] text-gray-600">
                  Method
                  <select
                    value={addDraft.method}
                    onChange={(ev) => setAddDraft(d => ({ ...d, method: ev.target.value }))}
                    className="w-full mt-0.5 px-2 py-1 text-sm border border-gray-300 rounded bg-white"
                    data-testid="payment-history-add-method"
                  >
                    {PAYMENT_METHODS.map((m) => (
                      <option key={m.key} value={m.key}>{m.label}</option>
                    ))}
                  </select>
                </label>
                <div className="flex justify-end gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => setAddOpen(false)}
                    className="text-[11px] font-semibold text-gray-600 hover:text-gray-900 px-2 py-1"
                    data-testid="payment-history-add-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={submitAdd}
                    disabled={addingPayment || !addDraft.amount || Number(addDraft.amount) <= 0}
                    className={`inline-flex items-center gap-1 text-[11px] font-bold text-white rounded px-3 py-1 disabled:opacity-50 ${isRefund ? 'bg-rose-600 hover:bg-rose-700' : 'bg-emerald-600 hover:bg-emerald-700'}`}
                    data-testid="payment-history-add-save"
                  >
                    {addingPayment ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                    {isRefund ? 'Record refund' : 'Log payment'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        {entries.length === 0 ? (
          <p className="text-xs text-gray-500 italic p-4">
            No individual payments recorded — the paid figure was set by editing the fee/paid inputs directly.
          </p>
        ) : (
          <ol className="divide-y max-h-72 overflow-auto">
            {[...entries].map((_, revIdx) => revIdx).reverse().map((originalIdx) => {
              const e = entries[originalIdx];
              const when = e.at ? new Date(e.at) : null;
              const key = `${e.at || 'unknown'}-${e.method || ''}-${e.amount || 0}-${originalIdx}`;
              const isDeleting = deletingIndex === originalIdx;
              const isEditingRow = editingIndex === originalIdx;
              const isDraftRow = editRowIdx === originalIdx;
              // Source badge — helps staff spot import-parser oopsies at a
              // glance. Colour-matched to the wizard preview legend.
              const src = e.source;
              const srcTone = src === 'refund' ? 'bg-rose-100 text-rose-800'
                : src === 'deposit' ? 'bg-sky-100 text-sky-800'
                : src === 'log' ? 'bg-emerald-100 text-emerald-800'
                : src === 'notes' ? 'bg-amber-100 text-amber-800'
                : src === 'manual' ? 'bg-violet-100 text-violet-800'
                : null;
              const srcLabel = src === 'refund' ? 'R'
                : src === 'deposit' ? 'D'
                : src === 'log' ? 'J'
                : src === 'notes' ? 'N'
                : src === 'manual' ? 'M'
                : null;
              if (isDraftRow) {
                return (
                  <li key={key} className="px-3 py-2 space-y-2 bg-amber-50/40" data-testid={`payment-history-edit-form-${originalIdx}`}>
                    <div className="text-[10px] uppercase tracking-wider font-bold text-amber-800">Editing entry</div>
                    <div className="grid grid-cols-2 gap-2">
                      <label className="text-[11px] text-gray-600">
                        Amount (£)
                        <input
                          type="number" step="0.01" min="0"
                          value={editDraft.amount}
                          onChange={(ev) => setEditDraft(d => ({ ...d, amount: ev.target.value }))}
                          className="w-full mt-0.5 px-2 py-1 text-sm border border-gray-300 rounded"
                          data-testid={`payment-history-edit-amount-${originalIdx}`}
                          autoFocus
                        />
                      </label>
                      <label className="text-[11px] text-gray-600">
                        Method
                        <input
                          type="text"
                          value={editDraft.method}
                          onChange={(ev) => setEditDraft(d => ({ ...d, method: ev.target.value }))}
                          placeholder="e.g. Bank Transfer"
                          className="w-full mt-0.5 px-2 py-1 text-sm border border-gray-300 rounded"
                          data-testid={`payment-history-edit-method-${originalIdx}`}
                        />
                      </label>
                    </div>
                    <label className="block text-[11px] text-gray-600">
                      Date
                      <input
                        type="date"
                        value={editDraft.at}
                        onChange={(ev) => setEditDraft(d => ({ ...d, at: ev.target.value }))}
                        className="w-full mt-0.5 px-2 py-1 text-sm border border-gray-300 rounded"
                        data-testid={`payment-history-edit-date-${originalIdx}`}
                      />
                    </label>
                    <div className="flex justify-end gap-2 pt-1">
                      <button
                        type="button"
                        onClick={cancelEdit}
                        className="text-[11px] font-semibold text-gray-600 hover:text-gray-900 px-2 py-1"
                        data-testid={`payment-history-edit-cancel-${originalIdx}`}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={saveEdit}
                        disabled={isEditingRow}
                        className="inline-flex items-center gap-1 text-[11px] font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded px-3 py-1 disabled:opacity-50"
                        data-testid={`payment-history-edit-save-${originalIdx}`}
                      >
                        {isEditingRow ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                        Save
                      </button>
                    </div>
                  </li>
                );
              }
              return (
                <li key={key} className="px-3 py-2 flex items-start gap-2" data-testid={`payment-history-entry-${originalIdx}`}>
                  {srcLabel && (
                    <span
                      className={`text-[10px] font-bold uppercase rounded px-1 mt-0.5 ${srcTone}`}
                      title={src === 'deposit' ? 'From spreadsheet Deposit column'
                        : src === 'log' ? 'From spreadsheet Payment log column'
                        : src === 'notes' ? 'Parsed from spreadsheet Notes column'
                        : 'Logged manually on the register'}
                    >
                      {srcLabel}
                    </span>
                  )}
                  <div className={`text-sm font-bold whitespace-nowrap tabular-nums ${Number(e.amount) < 0 ? 'text-rose-700' : 'text-gray-900'}`}>
                    {Number(e.amount) < 0 ? '−' : ''}£{Math.abs(Number(e.amount || 0)).toFixed(2)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold text-gray-800">
                      {PAYMENT_METHOD_LABEL[e.method] || e.method || 'Manual'}
                    </div>
                    <div className="text-[11px] text-gray-500 mt-0.5">
                      {when ? when.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                    </div>
                    {e.by_name && (
                      <div className="text-[11px] text-gray-500">by {e.by_name}</div>
                    )}
                  </div>
                  {onEdit && (
                    <button
                      type="button"
                      onClick={(ev) => { ev.stopPropagation(); beginEdit(originalIdx, e); }}
                      className="shrink-0 mt-0.5 p-1 rounded hover:bg-sky-50 text-gray-400 hover:text-sky-600 transition"
                      aria-label="Edit this payment"
                      title="Edit amount / method / date"
                      data-testid={`payment-history-edit-${originalIdx}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {onDelete && (
                    <button
                      type="button"
                      onClick={(ev) => { ev.stopPropagation(); onDelete(originalIdx, e); }}
                      disabled={isDeleting}
                      className="shrink-0 mt-0.5 p-1 rounded hover:bg-rose-50 text-gray-400 hover:text-rose-600 transition disabled:opacity-40"
                      aria-label="Delete this payment"
                      title="Remove this payment — recalculates the paid total"
                      data-testid={`payment-history-delete-${originalIdx}`}
                    >
                      {isDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                    </button>
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </PopoverContent>
    </Popover>
  );
}

const gbp = (n) => `£${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Shows exactly where a balance comes from: fee → logged payments → difference, plus the sheet columns it was imported from. */
export function BalanceBreakdown({ reg, form }) {
  const fee = Number(form.payment_total || 0);
  const paid = Number(form.payment_received || 0);
  const history = reg.payment_history || [];
  const historySum = Math.round(history.reduce((a, e) => a + Number(e.amount || 0), 0) * 100) / 100;
  const unexplained = Math.round((paid - historySum) * 100) / 100;
  const bd = reg.booking_details || {};
  const sheet = [['Deposit paid', bd.deposit_paid], ['Balance on booking', bd.balance_on_booking], ['Final balance', bd.final_balance]].filter(([, v]) => v !== undefined && v !== null && String(v).trim() !== '');
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs space-y-1.5" data-testid="balance-breakdown">
      <p className="font-bold text-gray-700 uppercase tracking-wider text-[10px]">How this balance is worked out</p>
      <div className="flex justify-between"><span>Course fee</span><span className="font-semibold">{gbp(fee)}</span></div>
      {history.length ? history.map((e, i) => (
        <div key={`${e.source || "paid"}-${e.at || ""}-${i}`} className="flex justify-between text-gray-600 pl-3">
          <span>− {e.source === 'deposit' ? 'Deposit' : e.source === 'sheet' ? 'Remaining balance per spreadsheet' : 'Paid'}{e.method ? ` · ${e.method}` : ''}{e.at ? ` · ${new Date(e.at).toLocaleDateString('en-GB')}` : ''}</span>
          <span>{gbp(e.amount)}</span>
        </div>
      )) : <div className="text-gray-500 pl-3">No individual payments logged</div>}
      {unexplained !== 0 && (
        <div className="flex justify-between pl-3 text-amber-800" data-testid="balance-breakdown-unexplained">
          <span>− {unexplained > 0 ? 'Recorded as paid but not itemised' : 'Itemised payments exceed "Paid so far"'}</span>
          <span>{gbp(Math.abs(unexplained))}</span>
        </div>
      )}
      <div className="flex justify-between border-t border-gray-200 pt-1.5 font-bold" data-testid="balance-breakdown-result">
        <span>Balance owed = fee − paid</span><span className={fee - paid > 0 ? 'text-rose-700' : 'text-emerald-700'}>{gbp(Math.max(0, fee - paid))}</span>
      </div>
      {sheet.length > 0 && (
        <p className="text-[11px] text-gray-500 pt-1">From spreadsheet: {sheet.map(([k, v]) => `${k} ${v}`).join(' · ')}{bd.notes ? ` · Notes: "${bd.notes}"` : ''}</p>
      )}
    </div>
  );
}

